import { Selector } from '@ngxs/store';
import _ from 'lodash';
import 'src/app/samwebDescription/samweb/extension-methods/array-extensions';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { UserStatusEnum } from 'src/app/samwebDescription/samwin/UserStatusEnum';
import { SamwinAgentListState } from 'src/app/store/samwin/samwin-agent-list/samwin-agent-list.state';
import { SamwinLinesStateSelector } from 'src/app/store/samwin/samwin-lines/samwin-lines.selector';
import { SamwinUserStatusState } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.state';
import { UserStatusResetConstants } from 'src/app/store/samwin/samwin-user-status/user-status-reset-constants';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { AgentListEntryRemoteDto } from 'src/generated/signalR/agent-list-entry-remote-dto';
import { StatusType } from 'src/generated/signalR/status-type';
import { UserStatusInfoRemoteDto } from 'src/generated/signalR/user-status-info-remote-dto';

export class SamwinUserStatusSelectors {
   @Selector([SamwinAgentListState.getAgentDetailsForUserId, SamwinUserStatusState.all])
   public static getUserStatusInfoByUserId(
      getAgentDetailsForUserId: (userId: number) => null | AgentListEntryRemoteDto,
      allStates: UserStatusInfoRemoteDto[]
   ): (userId: number) => UserStatusInfoRemoteDto {
      return (userId: number) => {
         const status = getAgentDetailsForUserId(userId)?.userStatus;
         if (_.isNil(status)) {
            return null;
         }
         return allStates.find((info) => info.id === status.id);
      };
   }

   @Selector([SamwinUserStatusState.all])
   public static getAllSelectableStatusInfos(all: UserStatusInfoRemoteDto[]) {
      return all
         .filter((info) => {
            return info.id !== UserStatusEnum.BUSY.value && info.id !== UserStatusEnum.CALL_WAITING.value;
         })
         .sortUserStatusInfoRemoteDtos();
   }

   @Selector([
      SamwinUserStatusState.all,
      SamwinUserStatusState.currentStatusInfo,
      EnabledFeaturesState.isFeatureEnabled(FeatureEnum.CCO_PERM_USE_PAUSE),
      EnabledFeaturesState.isFeatureEnabled(FeatureEnum.CCO_PERM_MAY_SET_ACW),
      SamwinLinesStateSelector.isAnyLineActivityInProgress,
   ])
   public static getAllFollowUpStatusInfos(
      statusInfos: UserStatusInfoRemoteDto[],
      currentStatusInfo: UserStatusInfoRemoteDto,
      mayUsePause: boolean,
      mayUseAcwEnabled: boolean,
      isAnyLineActivityInProgress: boolean
   ) {
      // remove the busy and call waiting status statuses
      let followUpStates = statusInfos.filter((info) => {
         return info.id !== UserStatusEnum.BUSY.value && info.id !== UserStatusEnum.CALL_WAITING.value;
      });

      // while being BUSY or LOGGED_OFF/BUSY (logged-off but in a call)
      // it is only allowed to switch to other logged-off states
      if (!_.isNil(currentStatusInfo)) {
         if (currentStatusInfo?.id === UserStatusEnum.BUSY.value || (currentStatusInfo.type === StatusType.LOGGED_OFF && isAnyLineActivityInProgress)) {
            followUpStates = followUpStates.filter((status) => status.type === StatusType.LOGGED_OFF);
         }
      }

      if (!mayUsePause) {
         followUpStates = followUpStates.filter((status) => status.id !== UserStatusEnum.DND.value);
      }

      if (!mayUseAcwEnabled) {
         followUpStates = followUpStates.filter((status) => status.id !== UserStatusEnum.ACW.value);
      }

      return followUpStates;
   }

   @Selector([SamwinUserStatusSelectors.getAllFollowUpStatusInfos, SamwinUserStatusState.currentStatusInfo, SamwinUserStatusState.getAllowedStatusResets])
   public static getAllowedFollowUpState(
      allFollowUpStatusInfos: UserStatusInfoRemoteDto[],
      currentStatusInfo: UserStatusInfoRemoteDto,
      allowedStatusResets: number
   ) {
      return (requestedUserState: number) => {
         const isAllowed = allFollowUpStatusInfos.filter((status) => status?.id === requestedUserState && status?.id !== currentStatusInfo?.id).length > 0;
         const isActionACW = requestedUserState === UserStatusEnum.ACW.value;
         const isCurrentACW = currentStatusInfo?.id === UserStatusEnum.ACW.value;
         const canAcwBeSetForCall = allowedStatusResets > 0 || allowedStatusResets === UserStatusResetConstants.UNLIMITED;

         return isAllowed || (canAcwBeSetForCall && isActionACW && isCurrentACW);
      };
   }

   @Selector([SamwinUserStatusSelectors.getAllFollowUpStatusInfos])
   public static getStateById(allFollowUpStatusInfos: UserStatusInfoRemoteDto[]) {
      return (userStateId: number) => {
         return allFollowUpStatusInfos.find((status) => status?.id === userStateId);
      };
   }
}
